import { useState } from "react"
import { Loader2 } from "lucide-react"

import AuthAlert from "@/components/tuw/auth/AuthAlert"
import { AuthHeading } from "@/components/tuw/auth/AuthHeading"
import { TextField } from "@/components/tuw/auth/fields"
import { Button } from "@/components/ui/button"
import { correctEmail, signInToContinue } from "@/lib/openHouse/guest"
import { EMAIL_CORRECTION as E } from "@/lib/openHouse/wording"

// Forwarding address (spec P3-H8; BRIEF-2 step 7; guest-wording §5): the
// waiting screen's form for correcting the address its confirmation email
// went to (OpenHouse::ConfirmationsController#correct). The waiting screen
// owns the state they share: `waiting`, and the cooldown resend and
// correction share (`cooldown`, `onCooldown(seconds)`).
//
// onCorrected(body, { delivered }) takes the server's { email, waiting } back
// to the waiting screen; onRefusal(err) hands it the refusals it shows itself
// (not_pending, expired, wrong_account, too_many_attempts); onUsedUp() records
// that the reservation's emails are used up; onCancel() goes back.
//
// `correct(address)` and `onSignIn()` default to the post's correction and
// continuation; `words` ({ unchanged, inUseBody }) replaces those lines, and
// with it the post's own lines (details kept, deadline) are left out.

const EMAIL = /^[^@\s]+@[^@\s]+$/ // Devise.email_regexp
const EMAIL_LIMIT = 254
const MUTED = "text-[13px] leading-[1.55] text-muted-foreground"
const HANDED_BACK = ["not_pending", "expired", "wrong_account", "too_many_attempts"]

export default function EmailCorrection({
  waiting, cooldown, onCooldown, onCorrected, onRefusal, onUsedUp, onCancel,
  correct = correctEmail, onSignIn = signInToContinue, words,
}) {
  const [email, setEmail] = useState("")
  const [fieldError, setFieldError] = useState(null)
  const [failed, setFailed] = useState(false)
  const [inUse, setInUse] = useState(false)
  const [saving, setSaving] = useState(false)

  const submit = async (event) => {
    event?.preventDefault()
    if (saving || cooldown > 0) return
    setFailed(false)
    const address = email.trim().toLowerCase()
    if (!address) return setFieldError(E.required)
    if ([...address].length > EMAIL_LIMIT || !EMAIL.test(address)) return setFieldError(E.invalid)

    setFieldError(null)
    setSaving(true)
    try {
      onCorrected(await correct(address), { delivered: true })
    } catch (err) {
      setSaving(false)
      if (err.reason === "delivery_failed" && err.data?.waiting) return onCorrected(err.data, { delivered: false })
      if (err.reason === "email_unchanged") return setFieldError(words?.unchanged ?? E.unchanged)
      if (err.reason === "email_invalid") return setFieldError(E.invalid)
      if (err.reason === "email_in_use") return setInUse(true)
      if (err.reason === "cooldown") return onCooldown(err.data?.retry_after_seconds || 0)
      if (err.reason === "too_many_resends") return onUsedUp()
      if (HANDED_BACK.includes(err.reason)) return onRefusal(err)
      setFailed(true)
    }
  }

  if (inUse) {
    return (
      <>
        <AuthHeading title={E.inUse.heading}>{words?.inUseBody ?? E.inUse.body}</AuthHeading>
        <div className="flex flex-col gap-2">
          <Button onClick={() => onSignIn()}>{E.inUse.signIn}</Button>
          <Button variant="outline" onClick={() => setInUse(false)}>{E.inUse.change}</Button>
        </div>
      </>
    )
  }

  return (
    <>
      <AuthHeading title={E.heading}>{E.current(waiting.email)}</AuthHeading>
      {failed && (
        <div className="flex flex-col items-start gap-2">
          <AuthAlert tone="bad">{E.failed(waiting.email)}</AuthAlert>
          <Button variant="outline" onClick={() => submit()} disabled={saving || cooldown > 0}>{E.retry}</Button>
        </div>
      )}
      <form onSubmit={submit} noValidate className="flex flex-col gap-[14px]">
        <TextField id="oh-correct-email" type="email" label={E.label} autoComplete="email" value={email}
          hint={E.help} error={fieldError} onChange={(e) => setEmail(e.target.value)} />
        {!words && <p className={MUTED}>{E.preserved}</p>}
        <p className={MUTED}>{E.oldLinks}</p>
        {!words && waiting.deadlineShown && <p className={MUTED}>{E.deadline(waiting.deadlineShown)}</p>}
        {cooldown > 0 && <p className={MUTED}>{E.cooldown(cooldown)}</p>}
        {!waiting.resendsUsedUp && (
          <Button type="submit" disabled={saving || cooldown > 0} className="gap-1.5">
            {saving && <Loader2 className="size-4 animate-spin" />}
            {saving ? E.submitting : E.submit}
          </Button>
        )}
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>{E.cancel}</Button>
      </form>
    </>
  )
}
